import { getNestedValue, interpolateString } from './utils/helpers';
import { devLog } from "../utils/devLog";
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from './utils/constants';

// Translation cache
const translationCache = new Map();

/**
 * Loads translation resources for a given language
 * @param {string} language - Language code
 * @returns {Promise<Object>} Translation object
 */
export const loadTranslations = async (language) => {
  // Check cache first
  if (translationCache.has(language)) {
    return translationCache.get(language);
  }

  try {
    // Dynamic import for lazy loading
    const translations = await import(`./resources/${language}.json`);
    const translationData = translations.default || translations;
    
    // Cache the translations
    translationCache.set(language, translationData);
    return translationData;
  } catch (error) {
    console.warn(`Failed to load translations for language: ${language}`, error);
    
    // Fallback to default language if current language fails
    if (language !== DEFAULT_LANGUAGE) {
      return loadTranslations(DEFAULT_LANGUAGE);
    }
    
    // Return empty object if even default fails
    return {};
  }
};

/**
 * Translation function
 * @param {string} key - Translation key (dot notation)
 * @param {Object} options - Translation options
 * @param {Object} options.variables - Variables for interpolation
 * @param {number} options.count - Count for pluralization
 * @param {string} options.defaultValue - Default value if key not found
 * @param {Object} translations - Translation object
 * @param {string} language - Current language
 * @returns {string} Translated string
 */
export const translate = (key, options = {}, translations = {}, language = DEFAULT_LANGUAGE) => {
  const { variables = {}, count, defaultValue } = options;
  
  // Get the translation value
  let value = getNestedValue(translations, key, defaultValue || key);
  
  // Handle pluralization if count is provided
  if (count !== undefined && typeof value === 'object' && value.singular && value.plural) {
    value = count === 1 ? value.singular : value.plural;
  }
  
  // Interpolate variables
  if (typeof value === 'string' && Object.keys(variables).length > 0) {
    value = interpolateString(value, variables);
  }
  
  return value || key;
};

/**
 * Preload translations for better performance
 * @param {string[]} languages - Array of language codes to preload
 */
export const preloadTranslations = async (languages = Object.values(SUPPORTED_LANGUAGES)) => {
  const loadPromises = languages.map(language => loadTranslations(language));
  
  try {
    await Promise.all(loadPromises);
    devLog('Translations preloaded successfully');
  } catch (error) {
    console.warn('Failed to preload some translations:', error);
  }
};

/**
 * Clear translation cache (useful for development)
 */
export const clearTranslationCache = () => {
  translationCache.clear();
};

export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from './utils/constants'; 